import React, { useMemo, useState } from "react";
import type { User, MarketplaceEntry } from "../types/index";
import { slugifyOrNull } from "@skills-marketplace/contracts/slug";
import { isContributedRecord } from "../lib/contributed";
import {
  presignUpload,
  uploadFileToS3,
  publishSkill,
  GitHubProxyError,
  NetworkBlockedError,
} from "../lib/github-proxy-client";
import FileDropZone from "../components/FileDropZone";
import type { SkillFile } from "../components/FileDropZone";
import {
  classifyPath,
  summarizeDisallowedPaths,
  ruleClassFor,
  DISALLOWED_PAYLOAD_REMEDIATION,
} from "@skills-marketplace/contracts/payload-rules";
import { MAX_KEYWORDS, MAX_KEYWORD_LENGTH } from "@skills-marketplace/contracts/manifest-limits";
import { IS_DEV } from "../lib/env";
import { reportError, reportEvent } from "../lib/newrelic";
import styles from "./ContributeSkillPage.module.css";

type SubmitPhase =
  | "idle"
  | "uploading-files"
  | "analyzing-skill"
  | "publishing"
  | "done"
  | "error";

interface SuccessResult {
  contributorName: string;
  submissionDate: string;
  installName: string;
  /**
   * False when the commit landed but the processor was never notified (the SQS
   * publish failed after retries). Drives a warning state instead of the normal
   * "will appear shortly" copy; the skill won't appear without an admin reprocess,
   * so routing "View in Catalog" into the processing state would loop forever.
   */
  catalogQueued: boolean;
  catalogQueueMessage?: string;
}

export interface EditExistingFile {
  name: string;
  path: string;
  content: string;
}

export interface EditData {
  installKey: string;
  skillName: string;
  description: string;
  keywords: string[];
  existingFiles: EditExistingFile[];
}

interface ContributeSkillPageProps {
  onBack: () => void;
  /** Label for the back link. Defaults to "Back to Marketplace". */
  backLabel?: string;
  onViewSkill?: (installName: string) => void;
  user: User | null;
  editData?: EditData;
  /**
   * Published catalog entries, used for the advisory name-availability check (#697).
   * Prop-drilled from AppContent (which already holds the catalog via useCatalog)
   * rather than read from context directly, so the page is unit-testable without
   * mounting a CatalogProvider. Defaults to an empty list — the check simply finds
   * nothing taken.
   */
  catalogEntries?: MarketplaceEntry[];
}

const MAX_NAME = 100;
const MAX_DESC = 2000;

function isTextContent(base64: string): boolean {
  try {
    const decoded = atob(base64);
    for (let i = 0; i < Math.min(decoded.length, 512); i++) {
      const code = decoded.charCodeAt(i);
      if (code === 0) return false;
    }
    return true;
  } catch {
    return false;
  }
}

function base64ToFile(name: string, base64: string): File {
  try {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
      bytes[i] = binary.charCodeAt(i);
    }
    return new File([bytes], name);
  } catch {
    throw new Error(`Failed to process file "${name}". Please remove and re-upload it.`);
  }
}

export default function ContributeSkillPage({
  onBack,
  backLabel,
  onViewSkill,
  user,
  editData,
  catalogEntries = [],
}: ContributeSkillPageProps) {
  const isEdit = !!editData;
  const backText = backLabel ?? "Back to Marketplace";

  const [devEmail, setDevEmail] = useState(user?.email ?? "");
  const [devName, setDevName] = useState(user?.name ?? "");

  const showDevIdentity = IS_DEV && !user;
  const effectiveUser: User | null = user
    ?? (devEmail ? { id: "dev", email: devEmail, name: devName || devEmail } : null);

  // Advisory name-availability check (#697): the loaded catalog already holds every
  // published installName, so a new contribution's name can be checked against it in
  // the browser with no extra request. This APPROXIMATES the server's authoritative
  // gate (upload.ts findForeignNameCollision) and is not a substitute for it — an MCP
  // or direct API caller never runs this, and it can diverge on names only the server
  // sees (hidden opt-out plugins are absent from the loaded catalog). "Taken" = any
  // published installName EXCEPT the caller's own contributed skills (renaming into a
  // name you already own is an edit, not a collision). Only consulted on a fresh
  // create (see validate()). Both the lookup key (slug) and this set are lowercased,
  // mirroring the server gate's case-insensitive compare (#697).
  const takenInstallNames = useMemo(() => {
    const mine = effectiveUser?.email?.toLowerCase();
    const taken = new Set<string>();
    for (const p of catalogEntries) {
      if (!p.installName) continue;
      const isMine =
        !!mine && isContributedRecord(p) && p.contributorEmail?.toLowerCase() === mine;
      if (!isMine) taken.add(p.installName.toLowerCase());
    }
    return taken;
  }, [catalogEntries, effectiveUser?.email]);

  const [skillName, setSkillName] = useState(editData?.skillName ?? "");
  const [description, setDescription] = useState(editData?.description ?? "");
  const [keywordInput, setKeywordInput] = useState("");
  const [keywords, setKeywords] = useState<string[]>(editData?.keywords ?? []);
  const [files, setFiles] = useState<SkillFile[]>([]);

  const [existingFiles, setExistingFiles] = useState<EditExistingFile[]>(
    editData?.existingFiles ?? [],
  );
  // Keyed by full skill-relative `path`, NOT basename: two existing files can
  // share a basename (a/index.js, b/index.js), and keying by name would collide
  // (edits to one bleed into the other, and one Remove nukes both).
  const [editedContent, setEditedContent] = useState<Record<string, string>>(() => {
    const initial: Record<string, string> = {};
    for (const f of editData?.existingFiles ?? []) {
      if (isTextContent(f.content)) {
        try {
          initial[f.path] = atob(f.content);
        } catch {
          // Corrupted base64 — treat as binary
        }
      }
    }
    return initial;
  });
  const [expandedFile, setExpandedFile] = useState<string | null>(null);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [phase, setPhase] = useState<SubmitPhase>("idle");
  const [submitError, setSubmitError] = useState("");
  const [success, setSuccess] = useState<SuccessResult | null>(null);

  if (!user && !IS_DEV) {
    return (
      <div className={styles.signinWrap}>
        <h2 className={styles.sectionHeading}>Sign in required</h2>
        <p className={styles.signinText}>
          You need to be signed in to contribute.
        </p>
        <button onClick={onBack} className={styles.linkButton}>
          &larr; {backText}
        </button>
      </div>
    );
  }

  function validate(): Record<string, string> {
    const e: Record<string, string> = {};
    if (!skillName.trim()) e.skillName = "Name is required.";
    else if (skillName.length > MAX_NAME)
      e.skillName = `Name must be ${MAX_NAME} characters or fewer.`;
    // Name-collision pre-check on a fresh create only (#697). On edit the name maps
    // to a skill the caller already owns, so a match is expected. The server rejects
    // the same collision with a 409; this surfaces it inline before the upload round
    // trip. Compare on the derived slug — the identity the server actually stores.
    else if (!isEdit) {
      const slug = slugifyOrNull(skillName);
      if (slug && takenInstallNames.has(slug))
        e.skillName = `The name "${slug}" is already taken by another plugin in the marketplace. Choose a different name.`;
    }
    if (!description.trim()) e.description = "Description is required.";
    else if (description.length > MAX_DESC)
      e.description = `Description must be ${MAX_DESC} characters or fewer.`;
    // Keyword caps are enforced at add-time (see addKeyword / handleKeywordPaste),
    // but edit mode pre-populates keywords from the raw committed manifest, which
    // predates this cap and can carry more than MAX_KEYWORDS (#394). Re-check on
    // submit so a legacy record gets inline feedback here instead of a bare 400
    // from the upload gate, which enforces the same caps.
    if (keywords.length > MAX_KEYWORDS)
      e.keywords = `Keywords must be ${MAX_KEYWORDS} or fewer — remove ${keywords.length - MAX_KEYWORDS}.`;
    else if (keywords.some((k) => k.length > MAX_KEYWORD_LENGTH))
      e.keywords = `Each keyword must be ${MAX_KEYWORD_LENGTH} characters or fewer.`;
    if (files.length === 0 && existingFiles.length === 0) {
      e.files = "At least one file is required.";
    } else {
      // Backstop for the FileDropZone add-time screen: block submission if any
      // file (a newly-added one, or an existing file loaded in edit mode) would
      // be refused by the backend payload screen (@contracts/payload-rules).
      // These share one repo, so a refused file deactivates every skill in it
      // (issue #369). Uses the SAME rule the backend enforces.
      const disallowed = [
        ...files.map((f) => f.relativePath),
        // Screen the full skill-relative path, not the basename: EditExistingFile.name
        // is just `path.split("/").pop()`, and the vendored/build rules match on path
        // SEGMENTS (node_modules/, __pycache__/, …), so a basename can never trip them.
        // buildFilesForUpload() commits existing files under this same `f.path`, so the
        // screened path and the committed path agree (no false blocks, no flattening).
        ...existingFiles.map((f) => f.path),
      ].filter((p) => classifyPath(p) !== null);
      if (disallowed.length > 0) {
        e.files =
          `These files can't be published and must be removed before submitting: ${summarizeDisallowedPaths(disallowed)}. ` +
          DISALLOWED_PAYLOAD_REMEDIATION;
        // Most rejections are caught by FileDropZone before submit; this guard is
        // the edit-mode path (existing files aren't re-screened by the drop zone).
        // Emit the same bounded event as the drop zone + backend so the control's
        // hit/false-positive rate is observable (issue #369 leading indicator).
        reportEvent("uploadPayloadRejected", {
          surface: "submit-guard",
          count: disallowed.length,
          rules: [...new Set(disallowed.map(ruleClassFor))].filter(Boolean).join(","),
        });
      }
    }
    return e;
  }

  function setKeywordError(message: string) {
    setErrors((prev) => ({ ...prev, keywords: message }));
  }

  function clearKeywordError() {
    setErrors((prev) => {
      if (!prev.keywords) return prev;
      const next = { ...prev };
      delete next.keywords;
      return next;
    });
  }

  function addKeyword() {
    const tag = keywordInput.trim().toLowerCase();
    if (!tag) return;
    // Give feedback instead of silently swallowing the add (#394): a silent no-op at
    // the cap or on an over-length keyword is the same "no feedback" failure this issue
    // set out to kill, just moved client-side. The input deliberately has no maxLength
    // attribute — a browser would silently clamp typed/single-paste text to the cap with
    // no message (and comma-paste already reports over-length via handleKeywordPaste), so
    // this branch is where typed/single-paste over-length input gets its feedback. Keep
    // the typed text (unlike the duplicate branch below) so the user can trim it down.
    if (tag.length > MAX_KEYWORD_LENGTH) {
      setKeywordError(`Each keyword must be ${MAX_KEYWORD_LENGTH} characters or fewer.`);
      return;
    }
    if (keywords.includes(tag)) {
      // Duplicate — nothing to add, but clear the box so the user isn't left wondering.
      setKeywordInput("");
      return;
    }
    if (keywords.length >= MAX_KEYWORDS) {
      setKeywordError(`You can add up to ${MAX_KEYWORDS} keywords.`);
      return;
    }
    setKeywords([...keywords, tag]);
    setKeywordInput("");
    clearKeywordError();
  }

  function handleKeywordKeyDown(e: React.KeyboardEvent) {
    if (e.key === "Enter" || e.key === ",") {
      e.preventDefault();
      addKeyword();
    }
  }

  function handleKeywordPaste(e: React.ClipboardEvent<HTMLInputElement>) {
    const pasted = e.clipboardData.getData("text");
    if (!pasted.includes(",")) return;
    e.preventDefault();
    const candidates = [...new Set(
      pasted
        .split(",")
        .map((t: string) => t.trim().toLowerCase())
        .filter((t: string) => t && !keywords.includes(t))
    )];
    const overLength = candidates.filter((t) => t.length > MAX_KEYWORD_LENGTH).length;
    const fitLength = candidates.filter((t) => t.length <= MAX_KEYWORD_LENGTH);
    // Math.max guards against a negative slice: edit mode can mount with more than
    // MAX_KEYWORDS keywords (legacy manifest), and slice(0, negative) counts from the
    // end — which would ADD keywords past the cap instead of adding none (#394).
    const remaining = Math.max(0, MAX_KEYWORDS - keywords.length);
    const added = fitLength.slice(0, remaining);
    const overCap = fitLength.length - added.length;
    setKeywords([...keywords, ...added]);
    setKeywordInput("");
    // Report what was dropped instead of silently losing it (#394): a paste that keeps
    // only the first N with no feedback is exactly the "201 then quiet loss" pattern
    // this issue set out to kill, relocated to the browser.
    const notes: string[] = [];
    if (overCap > 0) notes.push(`${overCap} skipped past the ${MAX_KEYWORDS}-keyword limit`);
    if (overLength > 0) notes.push(`${overLength} longer than ${MAX_KEYWORD_LENGTH} characters skipped`);
    if (notes.length > 0) setKeywordError(`Some keywords weren't added: ${notes.join("; ")}.`);
    else clearKeywordError();
  }

  function removeKeyword(index: number) {
    setKeywords(keywords.filter((_, i) => i !== index));
    // Removing a keyword may resolve an over-cap error — clear it now rather than
    // leaving a stale message until the next submit (mirrors handleExistingFileRemoved).
    clearKeywordError();
  }

  function handleFilesAdded(added: SkillFile[]) {
    const addedPaths = new Set(added.map((f) => f.relativePath));

    // Match existing files by full path (same key as editedContent/removal), so
    // re-adding docs/guide.md replaces that file, not every basename `guide.md`.
    setExistingFiles((prev) => prev.filter((f) => !addedPaths.has(f.path)));
    setEditedContent((prev) => {
      const next = { ...prev };
      for (const p of addedPaths) delete next[p];
      return next;
    });
    if (expandedFile && addedPaths.has(expandedFile)) {
      setExpandedFile(null);
    }

    const kept = files.filter((f) => !addedPaths.has(f.relativePath));
    setFiles([...kept, ...added]);
    setErrors((prev) => {
      const next = { ...prev };
      delete next.files;
      return next;
    });
  }

  function handleFileRemoved(index: number) {
    setFiles(files.filter((_, i) => i !== index));
  }

  function handleExistingFileRemoved(path: string) {
    setExistingFiles(existingFiles.filter((f) => f.path !== path));
    setEditedContent((prev) => {
      const next = { ...prev };
      delete next[path];
      return next;
    });
    if (expandedFile === path) setExpandedFile(null);
    // Removing the offending file may resolve the payload-screen block — clear the
    // stale error now instead of leaving it until the next submit attempt (CS-4).
    setErrors((prev) => {
      const next = { ...prev };
      delete next.files;
      return next;
    });
  }

  function buildFilesForUpload(): SkillFile[] {
    const allFiles: SkillFile[] = [...files];

    for (const ef of existingFiles) {
      const content = editedContent[ef.path];
      let file: File;
      if (content !== undefined) {
        file = new File([content], ef.name);
      } else {
        file = base64ToFile(ef.name, ef.content);
      }
      // Commit under the plugin-root-relative path (normalized in the edit-load,
      // AppContent.tsx), NOT the basename. Using the basename flattened nested files
      // (references/guide.md → guide.md) and made the submit guard (which screens
      // ef.path) disagree with what actually gets committed. relativePath flows
      // through presign → S3 staging key → commit with slashes preserved and the
      // commit builder re-adds a single `skills/<slug>/`, so screened path ===
      // committed path (issue #372).
      allFiles.push({ file, relativePath: ef.path });
    }

    return allFiles;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const validationErrors = validate();
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;
    if (!effectiveUser) return;

    setPhase("uploading-files");
    setSubmitError("");

    try {
      const allFiles = buildFilesForUpload();
      let stagingKeyPrefix: string | undefined;

      for (const sf of allFiles) {
        const contentType = sf.file.type || "application/octet-stream";
        const presign = await presignUpload(
          sf.relativePath,
          contentType,
          stagingKeyPrefix,
        );
        stagingKeyPrefix = presign.stagingKey.substring(
          0,
          presign.stagingKey.indexOf("/", "uploads/".length) + 1,
        );
        await uploadFileToS3(presign.uploadUrl, sf.file);
      }

      setPhase("analyzing-skill");

      await new Promise((r) => setTimeout(r, 500));
      setPhase("publishing");

      // Paths the user removed: an original existing file that is neither still kept
      // nor re-added via the drop zone (issue #372). Derived at submit time (no
      // separate removedPaths state) so remove-then-re-add is correct for free — a
      // re-added path is excluded here, so it's overwritten by the upload, not
      // deleted. Paths are plugin-root-relative (normalized in the edit-load), the
      // same base the server prepends `skills/<slug>/` onto. The manifest can never
      // appear (the edit-load excludes plugin.json).
      const deletedPaths = isEdit
        ? editData!.existingFiles
            .map((f) => f.path)
            .filter(
              (p) =>
                !existingFiles.some((ef) => ef.path === p) &&
                !files.some((nf) => nf.relativePath === p),
            )
        : [];

      // Send previousInstallKey on EVERY edit, not just renames (issue #404). It is
      // the only source identity in the request body, so it's how the server confirms
      // an edit targets the contributed-skills repo (a plain edit of a foreign record
      // must be rejected server-side, not just hidden client-side). Safe on a plain
      // edit: the trailing slug equals slugify(skillName), so the server's rename
      // branch stays false and there's no delete/recreate.
      const result = await publishSkill({
        stagingKeyPrefix: stagingKeyPrefix!,
        skillName: skillName.trim(),
        description: description.trim(),
        keywords,
        contributorEmail: effectiveUser.email,
        contributorName: effectiveUser.name ?? effectiveUser.email,
        ...(isEdit ? { previousInstallKey: editData!.installKey, deletedPaths } : {}),
      });

      setSuccess({
        contributorName: effectiveUser.name ?? effectiveUser.email,
        submissionDate: new Date().toLocaleDateString(),
        installName: result.installName,
        catalogQueued: result.catalogQueued,
        catalogQueueMessage: result.catalogQueueMessage,
      });
      setPhase("done");
    } catch (err) {
      setPhase("error");
      reportError(err, "contribute-skill-submit", { isEdit: String(isEdit) });
      if (err instanceof NetworkBlockedError) {
        // The request never reached the server — typically (issue #345) a WAF
        // block whose 403 carries no CORS headers, which the browser exposes
        // only as an opaque fetch rejection. github-proxy-client translates
        // that into NetworkBlockedError at the fetch boundary; give actionable
        // guidance instead of the raw "Failed to fetch" that got misread as a
        // backend outage. (Checked before GitHubProxyError — it's a subclass.)
        setSubmitError(
          "The request was blocked before it reached the server. This is often a security filter flagging something in the skill's content — a description or file that resembles code or a database query. Please review your content and retry; if it keeps failing, contact the marketplace team.",
        );
      } else if (err instanceof GitHubProxyError) {
        setSubmitError(err.message);
      } else {
        setSubmitError(err instanceof Error ? err.message : "Something went wrong. Please try again.");
      }
    }
  }

  if (phase === "done" && success) {
    return (
      <div className={styles.narrowWrap}>
        <div className={styles.successCard}>
          <h2 className={styles.successTitle}>
            {isEdit ? "Updated Successfully" : "Published Successfully"}
          </h2>
          <p className={styles.successMeta}>
            Contributed by <strong>{success.contributorName}</strong> on{" "}
            {success.submissionDate}
          </p>
          {success.catalogQueued ? (
            <p className={styles.introText}>
              {isEdit
                ? "Your changes are being processed and will appear in the catalog shortly."
                : "Your submission is being processed and will appear in the catalog shortly."}
            </p>
          ) : (
            // The commit landed but the processor was never notified (SQS publish
            // failed). It will NOT appear until an admin reprocess, so we surface the
            // server's explanation and suppress "View in Catalog"; routing there
            // would drop the contributor into a processing state that never resolves.
            <p className={styles.catalogWarning}>
              {success.catalogQueueMessage ??
                "Your files were saved, but the catalog update could not be started. It won't appear until the marketplace team reprocesses it. Please contact platform support."}
            </p>
          )}
          <div className={styles.successActions}>
            {onViewSkill && success.catalogQueued && (
              <button
                onClick={() => onViewSkill(success.installName)}
                className={styles.viewButton}
              >
                View in Catalog
              </button>
            )}
            <button onClick={onBack} className={styles.linkButton}>
              &larr; {backText}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isSubmitting = phase !== "idle" && phase !== "error";

  return (
    <div className={styles.narrowWrap}>
      <button onClick={onBack} className={styles.backLink}>
        &larr; {backText}
      </button>

      <h2 className={styles.sectionHeading}>
        {isEdit ? "Edit Plug-In or Skill" : "Contribute a Plug-In or Skill"}
      </h2>
      <p className={styles.introText}>
        {isEdit
          ? "Update your plug-in or skill's metadata and files."
          : "Share your AI plug-in or skill with the Cox Automotive community. Fill out the form below and upload your files."}
      </p>

      <form onSubmit={handleSubmit}>
        {showDevIdentity && (
          <div className={styles.devIdentity}>
            <p className={styles.devIdentityLabel}>
              Dev: Contributor identity (required)
            </p>
            <div className={styles.row}>
              <input
                type="text"
                value={devName}
                onChange={(e) => setDevName(e.target.value)}
                placeholder="Name"
                className={styles.devInput}
              />
              <input
                type="email"
                value={devEmail}
                onChange={(e) => setDevEmail(e.target.value)}
                placeholder="Email (required)"
                className={styles.devInput}
              />
            </div>
          </div>
        )}

        {/* Skill Name */}
        <div className={styles.field}>
          <label htmlFor="skillName" className={styles.fieldLabel}>
            Name <span className={styles.required}>*</span>
          </label>
          <input
            id="skillName"
            type="text"
            value={skillName}
            onChange={(e) => setSkillName(e.target.value)}
            maxLength={MAX_NAME}
            disabled={isSubmitting}
            placeholder="e.g. PR Review Assistant"
            className={errors.skillName ? `${styles.textInput} ${styles.inputError}` : styles.textInput}
          />
          {errors.skillName && (
            <p className={styles.fieldError}>
              {errors.skillName}
            </p>
          )}
          <p className={styles.charCount}>
            {skillName.length}/{MAX_NAME}
          </p>
        </div>

        {/* Description */}
        <div className={styles.field}>
          <label htmlFor="description" className={styles.fieldLabel}>
            Description <span className={styles.required}>*</span>
          </label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={MAX_DESC}
            disabled={isSubmitting}
            rows={3}
            placeholder="Describe what your plug-in or skill does..."
            className={errors.description ? `${styles.textArea} ${styles.inputError}` : styles.textArea}
          />
          {errors.description && (
            <p className={styles.fieldError}>
              {errors.description}
            </p>
          )}
          <p className={styles.charCount}>
            {description.length}/{MAX_DESC}
          </p>
        </div>

        {/* Keywords */}
        <div className={styles.field}>
          <label htmlFor="keywords" className={styles.fieldLabel}>
            Keywords
          </label>
          <div className={styles.row}>
            <input
              id="keywords"
              type="text"
              value={keywordInput}
              onChange={(e) => setKeywordInput(e.target.value)}
              onKeyDown={handleKeywordKeyDown}
              onPaste={handleKeywordPaste}
              disabled={isSubmitting}
              placeholder="Type a keyword and press Enter"
              className={styles.keywordInput}
            />
            <button
              type="button"
              onClick={addKeyword}
              disabled={isSubmitting || !keywordInput.trim()}
              className={styles.addButton}
            >
              Add
            </button>
          </div>
          <p className={styles.fieldHint}>
            Up to {MAX_KEYWORDS} keywords, {MAX_KEYWORD_LENGTH} characters each.
          </p>
          {errors.keywords && (
            <p className={styles.fieldError}>
              {errors.keywords}
            </p>
          )}
          {keywords.length > 0 && (
            <div className={styles.keywordList}>
              {keywords.map((kw, i) => (
                <span key={kw} className={styles.keywordChip}>
                  {kw}
                  <button
                    type="button"
                    onClick={() => removeKeyword(i)}
                    className={styles.keywordRemove}
                  >
                    x
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Existing files (edit mode) */}
        {existingFiles.length > 0 && (
          <div className={styles.field}>
            <div className={styles.fieldLabel}>
              Existing Files
            </div>
            <div className={styles.fileList}>
              {existingFiles.map((ef) => {
                const isExpanded = expandedFile === ef.path;
                const isText = isTextContent(ef.content);
                // Show the file's full skill-relative path (not just the basename)
                // so a block message naming `node_modules/ (12 files)` maps to a
                // row the user can find and remove.
                const blocked = classifyPath(ef.path) !== null;
                return (
                  <div
                    key={ef.path}
                    className={blocked ? `${styles.fileRow} ${styles.fileRowBlocked}` : styles.fileRow}
                  >
                    <div className={styles.fileRowHeader}>
                      {isText ? (
                        <button
                          type="button"
                          onClick={() => setExpandedFile(isExpanded ? null : ef.path)}
                          aria-expanded={isExpanded}
                          className={styles.fileToggle}
                        >
                          <span className={styles.fileToggleIcon}>
                            {isExpanded ? "▼" : "▶"}
                          </span>
                          <span className={styles.fileName}>{ef.path}</span>
                        </button>
                      ) : (
                        <>
                          <span className={styles.fileNameBinary}>
                            {ef.path}
                          </span>
                          <span className={styles.binaryTag}>
                            Binary file
                          </span>
                        </>
                      )}
                      {blocked && (
                        <span className={styles.blockedTag}>Not allowed — remove</span>
                      )}
                      <button
                        type="button"
                        onClick={() => handleExistingFileRemoved(ef.path)}
                        disabled={isSubmitting}
                        className={styles.removeButton}
                      >
                        Remove
                      </button>
                    </div>
                    {isExpanded && isText && (
                      <textarea
                        value={editedContent[ef.path] ?? ""}
                        onChange={(ev) =>
                          setEditedContent((prev) => ({ ...prev, [ef.path]: ev.target.value }))
                        }
                        disabled={isSubmitting}
                        className={styles.fileEditor}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* New files */}
        <div className={styles.fieldWide}>
          <label className={styles.fieldLabel}>
            {isEdit ? "Add New Files" : "Files"}{" "}
            {!isEdit && <span className={styles.required}>*</span>}
          </label>
          <FileDropZone
            files={files}
            onFilesAdded={handleFilesAdded}
            onFileRemoved={handleFileRemoved}
          />
          {errors.files && (
            <p className={styles.fieldError} role="alert">
              {errors.files}
            </p>
          )}
        </div>

        {/* Progress indicator */}
        {isSubmitting && (
          <div className={styles.progressBox}>
            <ProgressStep label="Uploading files" active={phase === "uploading-files"} done={phase !== "uploading-files"} />
            <ProgressStep label="Analyzing" active={phase === "analyzing-skill"} done={phase === "publishing"} />
            <ProgressStep label={isEdit ? "Updating" : "Publishing to marketplace"} active={phase === "publishing"} done={false} />
          </div>
        )}

        {/* Error state */}
        {phase === "error" && submitError && (
          <div className={styles.errorBox} role="alert">
            <p className={styles.errorTitle}>
              {isEdit ? "Update failed" : "Upload failed"}
            </p>
            <p className={styles.errorMessage}>
              {submitError}
            </p>
            <button type="submit" className={styles.retryButton}>
              Retry
            </button>
          </div>
        )}

        {/* Submit */}
        {phase === "idle" && (
          <button type="submit" className={styles.submitButton}>
            {isEdit ? "Update" : "Submit"}
          </button>
        )}
      </form>
    </div>
  );
}

function ProgressStep({
  label,
  active,
  done,
}: {
  label: string;
  active: boolean;
  done: boolean;
}) {
  const icon = done ? "✓" : active ? "●" : "○";
  const stateClass = done ? styles.stepDone : active ? styles.stepActive : "";
  const className = [styles.progressStep, stateClass].filter(Boolean).join(" ");
  return (
    <div className={className}>
      <span className={styles.stepText}>{icon}</span>
      <span className={styles.stepText}>{label}</span>
    </div>
  );
}
